import {
  useMutation,
  useMutationState,
  useQueryClient,
  type Mutation,
  type MutationCache,
  type QueryKey,
} from '@tanstack/react-query'
import { useCallback } from 'react'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import { useToast } from '../../platform/feedback/ToastProvider'
import { emitMarkedPaid } from './recurringAnalytics'
import { formatDue, localToday, type LocalDate } from './recurringRules'
import type { Bill, Entry } from './recurringWire'

/**
 * Why a mark-paid failed, as the sheet needs it:
 *
 * - `linked` — 409: the transaction already belongs to another live stream.
 *   `recurringId` names that bill (Review Focus 5).
 * - `cash-unavailable` — 422 on the cash path: a backend without D8's cash
 *   payments refuses an empty body.
 * - `off-grid` — 422 on a cash payment that named a period (#826): the date is
 *   not one of the bill's billing periods.
 * - `already-paid` — 409 on the cash path: another write (a second tab, a
 *   second device) paid this period first. Never worded as "try again": a
 *   retry would pay the NEXT period (S8). The hook has toasted it.
 * - `period-already-paid` — 409 `{detail:{status:'period_already_paid',due_date}}`
 *   on the transaction path: another payment holds that period (#576). The sheet
 *   words it with `dueDate` (null when the body's date is missing or malformed).
 * - `failed` — anything else. The hook has already rolled back and toasted.
 */
export class MarkPaidError extends Error {
  readonly kind: 'linked' | 'cash-unavailable' | 'off-grid' | 'already-paid' | 'period-already-paid' | 'failed'
  readonly recurringId: string | null
  /** The period the server says is taken; only on `period-already-paid`. */
  readonly dueDate: LocalDate | null
  constructor(kind: MarkPaidError['kind'], recurringId: string | null = null, cause?: unknown, dueDate: LocalDate | null = null) {
    super(`mark-paid-${kind}`, { cause })
    this.name = 'MarkPaidError'
    this.kind = kind
    this.recurringId = recurringId
    this.dueDate = dueDate
  }
}

export const MARK_PAID_FAILED = "Couldn't mark it paid. Try again."
/** NEW COPY (owner review): the cash 409. */
export const ALREADY_PAID = 'This one is already marked paid.'
/** NEW COPY (#826): the 422 for a period the bill does not have. */
export const OFF_GRID = "That date isn't one of this bill's payment dates."

/**
 * What the user chose, with the date it was paid — sent explicitly (Ruling 35),
 * never left to the server's `date.today()`, which is UTC (S17): at 20:00 in
 * Vancouver it is already tomorrow there. A link carries the transaction's own
 * date; cash carries the user's local today.
 */
export type MarkPaidChoice =
  | {
      transactionId: null
      paidDate: LocalDate
      /**
       * #826: a cash payment for THIS period (a skipped one) instead of the one
       * due on `nextDue`. null or absent: the period the server pays.
       */
      period?: LocalDate | null
    }
  // A transaction never names a period here: mark-paid refuses both, so a
  // transaction on a named period goes through `POST /recurring/{id}/payments`
  // (`useFilePayment`), and the pair does not compile.
  | { transactionId: string; paidDate: LocalDate }

/** The period a choice names, or null for the one the server pays. */
const periodOfChoice = (choice: MarkPaidChoice): LocalDate | null => (choice.transactionId === null ? (choice.period ?? null) : null)

/** Exactly what `onMutate` flipped, so a rollback undoes that and nothing else. */
type Flip = { queryKey: QueryKey; wrote: Entry; previous: Entry }

/** `{ detail: { recurring_id } }` — FastAPI's HTTPException body for the ownership 409. */
function owningStream(error: unknown): string | null {
  if (typeof error !== 'object' || error === null) return null
  const detail = (error as { detail?: unknown }).detail
  if (typeof detail !== 'object' || detail === null) return null
  const id = (detail as { recurring_id?: unknown }).recurring_id
  return typeof id === 'string' && id !== '' ? id : null
}

function isPeriodAlreadyPaid(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false
  const detail = (error as { detail?: unknown }).detail
  return typeof detail === 'object' && detail !== null && (detail as { status?: unknown }).status === 'period_already_paid'
}

/** The body's `due_date` when it is a real calendar date, else null (never rendered raw). */
function takenDueDate(error: unknown): LocalDate | null {
  const raw = ((error as { detail?: { due_date?: unknown } }).detail ?? {}).due_date
  if (typeof raw !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null
  const [y = 0, m = 0, d = 0] = raw.split('-').map(Number)
  const t = new Date(Date.UTC(y, m - 1, d))
  return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d ? raw : null
}

/** NEW COPY (owner review): both sentences, for a period another payment already holds. */
export function periodAlreadyPaidCopy(dueDate: LocalDate | null): string {
  return dueDate === null ? 'That period is already paid.' : `The period due ${formatDue(dueDate, 'long')} is already paid.`
}

async function postMarkPaid(billId: string, choice: MarkPaidChoice): Promise<string | null> {
  const { transactionId, paidDate } = choice
  const period = periodOfChoice(choice)
  let result
  try {
    result = await api.POST('/api/v1/recurring/{recurring_id}/mark-paid', {
      params: { path: { recurring_id: billId } },
      body:
        transactionId === null
          ? { paid_date: paidDate, ...(period !== null ? { period_due_date: period } : {}) }
          : { transaction_id: transactionId, paid_date: paidDate },
    })
  } catch (cause) {
    throw new MarkPaidError('failed', null, cause)
  }
  const { data, error, response } = result
  if (error === undefined && data !== undefined) return data.next_due_date
  const owner = response.status === 409 ? owningStream(error) : null
  if (owner !== null) throw new MarkPaidError('linked', owner, error)
  if (response.status === 409 && transactionId !== null && isPeriodAlreadyPaid(error)) {
    throw new MarkPaidError('period-already-paid', null, error, takenDueDate(error))
  }
  if (response.status === 409 && transactionId === null) throw new MarkPaidError('already-paid', null, error)
  if (response.status === 422 && transactionId === null && period !== null) throw new MarkPaidError('off-grid', null, error)
  if (response.status === 422 && transactionId === null) throw new MarkPaidError('cash-unavailable', null, error)
  throw new MarkPaidError('failed', null, error)
}

/** Resolves or rejects with an already-pending mutation's own outcome. Sends nothing. */
function settled(cache: MutationCache, mutation: Mutation<unknown, Error, unknown, unknown>): Promise<void> {
  return new Promise((resolve, reject) => {
    let unsubscribe = () => {}
    const check = () => {
      if (mutation.state.status === 'success') {
        unsubscribe()
        resolve()
      } else if (mutation.state.status === 'error') {
        unsubscribe()
        reject(mutation.state.error)
      }
    }
    unsubscribe = cache.subscribe((event) => {
      if (event.mutation === mutation) check()
    })
    check()
  })
}

const isEntries = (d: unknown): d is Entry[] => Array.isArray(d)

/**
 * M7-R14, D8: mark one period of `bill` paid, with a transaction or as cash.
 *
 * **The in-flight lock (Review Focus 1, S8).** A repeated cash call never
 * 409s — the server pays the NEXT period. So while one request for this bill is
 * out, a second `markPaid` sends nothing: it waits on the first and settles with
 * its outcome. The lock is the pending mutation in the query client's mutation
 * cache, keyed `keys.recurring.markPaid(bill.id)` — never component state —
 * so a row that unmounts mid-request and remounts still sees it (`inFlight`
 * comes from `useMutationState` on the same key), and navigating away cannot
 * unlock a second send. The check is synchronous: TanStack dispatches
 * `pending` before `mutateAsync` first yields, so two calls in one tick are
 * one request.
 *
 * **Optimistic on cash only, then the server's word** (Ruling 37). A cash
 * payment settles the period it names (a skipped one, #826) or else the one due
 * on `bill.nextDue`, so that calendar entry turns `paid` at once (the row leaves Overdue instantly; no motion, M0-R20).
 * A LINK is not optimistic: the server files a transaction under the period
 * nearest its own date (`snap_to_grid` / `choose_period`), which for a weekly
 * or biweekly bill is often not `nextDue`, and guessing would show the wrong
 * row paid. The lock still holds, and the settle refetch shows the truth. On
 * success the bill's `nextDue` becomes the response's `next_due_date`; it is
 * never computed here (S18), and a skipped period's response is the unchanged one. On failure exactly the entries this call flipped are
 * put back — and only while they still hold what it wrote — so an overlapping
 * mark-paid on another bill, or a refetch that already landed, is never undone.
 * On settle the whole `['recurring']` root is invalidated (the
 * list, every month including next month's header, the detail and the
 * payments), plus the transaction reads when a transaction was linked — it
 * now carries a stream id.
 *
 * Mutation-level callbacks, so the rollback, toast and invalidation still run
 * when the row that started it has unmounted.
 */
export function useMarkPaid(bill: Bill): { markPaid: (choice: MarkPaidChoice) => Promise<void>; inFlight: boolean } {
  const client = useQueryClient()
  const toast = useToast()
  const mutationKey = keys.recurring.markPaid(bill.id)
  const inFlight = useMutationState({ filters: { mutationKey, exact: true, status: 'pending' } }).length > 0

  const mutation = useMutation<string | null, MarkPaidError, MarkPaidChoice, Flip[]>({
    mutationKey,
    mutationFn: (choice) => postMarkPaid(bill.id, choice),
    onMutate: async (choice) => {
      const flips: Flip[] = []
      // A link is filed by the server under the period nearest its date: no guess.
      if (choice.transactionId !== null) return flips
      await client.cancelQueries({ queryKey: keys.recurring.calendars() })
      // A named period flips that row; otherwise the one the server pays.
      const due = periodOfChoice(choice) ?? bill.nextDue
      if (due === null) return flips
      for (const [queryKey, data] of client.getQueriesData({ queryKey: keys.recurring.calendars() })) {
        if (!isEntries(data)) continue
        const previous = data.find((e) => e.bill.id === bill.id && e.dueDate === due && e.status !== 'paid')
        if (previous === undefined) continue
        client.setQueryData<Entry[]>(queryKey, data.map((e) => (e === previous ? { ...e, status: 'paid' as const } : e)))
        // Read back what the cache holds: structural sharing stores a copy, and
        // the rollback recognises its own write by reference.
        const wrote = client.getQueryData<Entry[]>(queryKey)?.find((e) => e.bill.id === bill.id && e.dueDate === due)
        if (wrote !== undefined) flips.push({ queryKey, wrote, previous })
      }
      return flips
    },
    onSuccess: (nextDue, choice) => {
      // From `bill` as this call found it: its `nextDue` is the period just paid.
      emitMarkedPaid(bill, choice, localToday())
      // Always the server's word (S18): paying a skipped period leaves the due
      // date alone (`due_date_after_payment` returns the current one), so the
      // response already says so — nothing is special-cased here.
      const withNext = (b: Bill): Bill => (b.id === bill.id ? { ...b, nextDue } : b)
      client.setQueryData<Bill[]>(keys.recurring.list(), (old) => old?.map(withNext))
      client.setQueryData<Bill>(keys.recurring.detail(bill.id), (old) => (old ? withNext(old) : old))
      client.setQueriesData({ queryKey: keys.recurring.calendars() }, (old: unknown) =>
        isEntries(old) ? old.map((e) => (e.bill.id === bill.id ? { ...e, bill: withNext(e.bill) } : e)) : old,
      )
    },
    onError: (error, _choice, flips) => {
      for (const { queryKey, wrote, previous } of flips ?? []) {
        client.setQueryData<Entry[]>(queryKey, (old) => old?.map((e) => (e === wrote ? previous : e)))
      }
      if (error.kind === 'failed') toast.show({ kind: 'error', message: MARK_PAID_FAILED })
      else if (error.kind === 'already-paid') toast.show({ kind: 'info', message: ALREADY_PAID })
    },
    // Not awaited: the caller (the sheet) settles on the server's answer, not
    // on the refetch behind it, and a slow refetch must not hold the lock.
    onSettled: (_data, _error, choice) => {
      void client.invalidateQueries({ queryKey: keys.recurring.all() })
      if (choice.transactionId !== null) void client.invalidateQueries({ queryKey: keys.transactions.all() })
    },
  })

  const { mutateAsync } = mutation
  const markPaid = useCallback(
    async (choice: MarkPaidChoice): Promise<void> => {
      const cache = client.getMutationCache()
      // Join only a request for the same period: a payment for another one
      // is not this call's outcome, so this one waits its turn (and sends
      // nothing while it is out, as before).
      const pending = cache.findAll({ mutationKey: keys.recurring.markPaid(bill.id), exact: true, status: 'pending' })[0]
      if (pending !== undefined) {
        const sameCall = periodOfChoice(pending.state.variables as MarkPaidChoice) === periodOfChoice(choice)
        if (sameCall) return settled(cache, pending)
        await settled(cache, pending).catch(() => undefined)
      }
      await mutateAsync(choice)
    },
    [client, bill.id, mutateAsync],
  )

  return { markPaid, inFlight }
}
